import { useLazyQuery, useQuery } from "@apollo/client";
import type { ChatCredentials } from "@shared/types/user";

import {
  GET_CHAT_CREDENTIALS_QUERY,
  GET_FOLDERS_ACCESS_TOKEN_QUERY,
  GET_INTERCOM_HASH_QUERY,
  GET_CALENDAR_SYNC_AUTH_LINK_QUERY,
  CURRENT_USER,
  GET_CURRENT_USER_AI_ASSISTANT_USAGE_QUERY,
  GET_AI_ASSISTANT_USAGE_QUERY,
  GET_AI_ASSISTANT_USAGE,
} from "~/api";
import { UserAiAssistantConfiguration } from "~/types/user";

// Box token TTL is 59 min; polling at half that guarantees two refresh
// opportunities per token lifetime and stays well inside the BE's 5-min
// refresh window.
const BOX_TOKEN_POLL_INTERVAL_MS = 30 * 60 * 1000;

export const useGetChatCredentials = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const { data, loading, refetch } = useQuery<{
    getChatCredentials: ChatCredentials | null;
  }>(GET_CHAT_CREDENTIALS_QUERY, { skip });
  return { credentials: data?.getChatCredentials ?? null, loading, refetch };
};

export const useGetFoldersAccessToken = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const { data, loading, refetch } = useQuery<{
    getFoldersAccessToken: string | null;
  }>(GET_FOLDERS_ACCESS_TOKEN_QUERY, {
    skip,
    fetchPolicy: "network-only",
    pollInterval: BOX_TOKEN_POLL_INTERVAL_MS,
  });
  return { token: data?.getFoldersAccessToken ?? null, loading, refetch };
};

export const useGetIntercomHash = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const { data, loading } = useQuery<{ getIntercomHash: string | null }>(
    GET_INTERCOM_HASH_QUERY,
    {
      skip,
      fetchPolicy: "cache-first",
    },
  );
  return { hash: data?.getIntercomHash ?? null, loading };
};

export const useGetCalendarSyncAuthLink = () => {
  const [
    getUrl,
    { data: { getCalendarSyncAuthUrl = "" } = {}, loading, error },
  ] = useLazyQuery(GET_CALENDAR_SYNC_AUTH_LINK_QUERY);
  return {
    loading,
    error,
    url: getCalendarSyncAuthUrl,
    getAuthUrl: async () => getUrl(),
  };
};

export const useGetCurrentUserAiAssistantUsage = () => {
  const { data, loading, refetch } = useQuery<{
    [CURRENT_USER]: {
      consumedRecordingSeconds: number;
      aiAssistantConfiguration: UserAiAssistantConfiguration;
    };
  }>(GET_CURRENT_USER_AI_ASSISTANT_USAGE_QUERY);

  return { data: data?.[CURRENT_USER], loading, refetch };
};

export const useGetAiAssistantUsage = (oemId: string) => {
  const { data, loading, refetch } = useQuery<{
    [GET_AI_ASSISTANT_USAGE]: {
      allowedQueries: number;
      consumedQueries: number;
    };
  }>(GET_AI_ASSISTANT_USAGE_QUERY, {
    skip: !oemId,
    variables: {
      oemId,
    },
  });

  return { data: data?.[GET_AI_ASSISTANT_USAGE], loading, refetch };
};
